import {
  syncBookingFromAcceptance,
  syncBookingFromWebSocket,
} from "@/hooks/queries/use-bookings-query";
import { syncChatMessagesFromFetch } from "@/hooks/queries/use-messages-query";
import { queryClient } from "@/lib/query-client";
import { queryKeys } from "@/lib/query-keys";
import bookingService, { Booking } from "@/lib/services/bookings";
import messageService from "@/lib/services/messages";
import { NotificationData } from "@/lib/types/websocket";
import { useAuthStore } from "@/stores/use-auth-store";
import { usePendingOffersStore } from "@/stores/use-pending-offers-store";
import { Router } from "expo-router";

function syncBookingTrackInBackground(
  bookingId: number,
  { applyAcceptance = false }: { applyAcceptance?: boolean } = {},
): void {
  if (applyAcceptance) {
    const detail = queryClient.getQueryData<Booking>(
      queryKeys.bookings.detail(bookingId),
    );
    const track = queryClient.getQueryData<{ booking: Booking }>(
      queryKeys.bookings.track(bookingId),
    );
    const cached = detail ?? track?.booking;

    if (cached) {
      syncBookingFromAcceptance(queryClient, cached);
    }
  }

  void bookingService
    .getBookingTrack(bookingId)
    .then(({ booking }) => {
      syncBookingFromWebSocket(queryClient, booking);
    })
    .catch((error) => {
      console.warn("Booking track sync failed", error);
    });
}

function syncBookingChatInBackground(bookingId: number): void {
  void messageService
    .getMessages(bookingId)
    .then((messages) => {
      syncChatMessagesFromFetch(queryClient, bookingId, messages);
    })
    .catch((error) => {
      console.warn("Booking chat sync failed", error);
    });
}

export function dedupeNotificationKey(data: NotificationData): string {
  if (data.delivery_id) {
    return `delivery:${data.delivery_id}`;
  }

  return `${data.type}:${data.booking_id ?? ""}:${data.offer_id ?? ""}:${data.quote_id ?? ""}`;
}

export interface NotificationNavigationContext {
  router: Pick<Router, "push" | "replace">;
  segments: string[];
  handledKeys: Set<string>;
  currentOfferBookingId?: number;
  navigate?: boolean;
}

export function shouldSkipNotificationNavigation(
  data: NotificationData,
  context: Pick<
    NotificationNavigationContext,
    "handledKeys" | "currentOfferBookingId"
  >,
): boolean {
  const key = dedupeNotificationKey(data);
  if (context.handledKeys.has(key)) {
    return true;
  }

  context.handledKeys.add(key);

  if (
    data.type === "new_booking" &&
    context.currentOfferBookingId === data.booking_id
  ) {
    return true;
  }

  return false;
}

export function navigateFromNotification(
  data: NotificationData,
  context: NotificationNavigationContext,
): void {
  const navigate = context.navigate ?? false;
  const { router, segments } = context;

  if (data.type === "new_booking") {
    usePendingOffersStore.getState().addOffer({
      offer_id: data.offer_id ?? data.booking_id!,
      booking_id: data.booking_id!,
      vehicle_id: data.vehicle_id,
      from_address: data.from_address,
      to_address: data.to_address,
      service_type: typeof data.service_type === "string" ? data.service_type : undefined,
      scheduled_at: typeof data.scheduled_at === "string" ? data.scheduled_at : undefined,
      expires_at: data.expires_at,
      priority: data.priority ?? data.is_priority,
    });

    if (
      !navigate ||
      shouldSkipNotificationNavigation(data, {
        handledKeys: context.handledKeys,
        currentOfferBookingId: context.currentOfferBookingId,
      })
    ) {
      return;
    }
  }

  switch (data.type) {
    case "new_booking":
      if (data.booking_id && data.vehicle_id) {
        if (segments[0] === "driver-booking-response") {
          return;
        }

        router.push({
          pathname: `/driver-booking-response/${data.booking_id}` as never,
          params: { vehicleId: data.vehicle_id },
        });
      }
      break;

    case "new_quote":
      if (data.booking_id) {
        if (segments[2] === "quotes") {
          return;
        }

        router.push(`/bookings/${data.booking_id}/quotes`);
      }
      break;

    case "new_scheduled_booking":
      queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.scheduledJobs(),
      });

      if (navigate && data.booking_id && data.vehicle_id) {
        router.push({
          pathname: `/driver-booking-response/${data.booking_id}` as never,
          params: { vehicleId: data.vehicle_id },
        });
      }
      break;

    case "scheduled_job_start":
      if (data.booking_id) {
        useAuthStore.getState().setActiveBooking(data.booking_id);
        syncBookingTrackInBackground(data.booking_id);

        if (navigate && segments[2] !== "live-driving") {
          router.push(`/bookings/${data.booking_id}/live-driving`);
        }
      }
      break;

    case "quote_accepted":
      if (data.booking_id) {
        usePendingOffersStore
          .getState()
          .removeOffersForBooking(data.booking_id);

        if (data.booking_status === "scheduled") {
          queryClient.invalidateQueries({
            queryKey: queryKeys.bookings.scheduledJobs(),
          });

          if (navigate) {
            router.push(`/bookings/${data.booking_id}`);
          }
          return;
        }

        if (segments[2] === "live-driving") {
          return;
        }

        router.push(`/bookings/${data.booking_id}/live-driving`);
      }
      break;

    case "quote_countered":
      if (data.booking_id && data.quote_id) {
        router.push(`/bookings/${data.booking_id}/quotes`);
      }
      break;

    case "quote_rejected":
      if (data.booking_id && data.vehicle_id) {
        console.log("quote_rejected", data.booking_id, data.vehicle_id);
        router.push({
          pathname: `/driver-booking-response/${data.booking_id}` as never,
          params: { vehicleId: data.vehicle_id, rejected: "true" },
        });
      }
      break;

    case "booking_status_change":
    case "payment_received":
      if (data.booking_id) {
        syncBookingTrackInBackground(data.booking_id, {
          applyAcceptance: data.status === "accepted",
        });
      }

      if (data.status === "accepted") {
        if (segments[2] === "live-tracking") {
          return;
        }

        router.replace(`/bookings/${data.booking_id}/live-tracking`);
        return;
      }

      if (data.status === "cancelled") {
        useAuthStore.getState().setActiveBooking(null);

        if (segments[0] === "bookings" && segments[1] === "[id]") {
          return;
        }

        router.replace(`/bookings/${data.booking_id}`);
        return;
      }

      if (data.status === "completed" && navigate) {
        if (segments[0] === "rate-booking" && segments[1] === "[id]") {
          return;
        }

        router.push(`/rate-booking/${data.booking_id}`);
        return;
      }

      if (navigate && data.booking_id) {
        router.push(`/bookings/${data.booking_id}`);
      }
      break;

    case "request_rating":
      if (data.booking_id && navigate) {
        if (segments[0] === "rate-booking" && segments[1] === "[id]") {
          return;
        }

        router.replace(`/rate-booking/${data.booking_id}`);
      }
      break;

    case "message_received":
      if (data.booking_id) {
        syncBookingChatInBackground(data.booking_id);
      }

      if (data.booking_id && navigate) {
        if (segments[2] === "chat") {
          return;
        }

        router.push({
          pathname: `/bookings/[id]/chat` as never,
          params: { id: data.booking_id, user: data.user, reload: "true" },
        });
      }
      break;

    case "vehicle_status_change":
    case "document_status_change":
      if (data.vehicle_id) {
        router.push(`/vehicles/${data.vehicle_id}`);
      }
      break;

    default:
      break;
  }
}

export function parseNotificationData(data: unknown): NotificationData | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  return data as NotificationData;
}
